"use client";

import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import type { Testimonial } from "@/lib/seo/testimonials";
import type { SeoLocale } from "@/lib/seo/config";

/**
 * Social-proof carousel (Proposal 10). Uses embla (already a dependency). The
 * quotes themselves are server-provided and rendered into the DOM, so they are
 * crawlable even before hydration; embla only adds the swipe/arrow behaviour.
 */
export function TestimonialsCarousel({
  testimonials,
  locale,
}: {
  testimonials: Testimonial[];
  locale: SeoLocale;
}) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" });
  const [selected, setSelected] = useState(0);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  if (testimonials.length === 0) return null;

  return (
    <div className="relative">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {testimonials.map((t) => (
            <figure
              key={t.id}
              className="min-w-0 shrink-0 grow-0 basis-full md:basis-1/2 lg:basis-1/3 pl-4 first:pl-0"
            >
              <blockquote className="h-full rounded-xl border border-border bg-card p-6 flex flex-col">
                <div className="flex gap-0.5 mb-3" aria-hidden="true">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <Star
                      key={i}
                      className="w-4 h-4 fill-primary text-primary"
                    />
                  ))}
                </div>
                <p className="text-sm text-foreground/90 flex-1">
                  &ldquo;{t.quote[locale]}&rdquo;
                </p>
                <figcaption className="mt-4 text-sm">
                  <span className="font-semibold">{t.name}</span>
                  <span className="text-muted-foreground">
                    {" "}
                    · {t.context[locale]}
                  </span>
                </figcaption>
              </blockquote>
            </figure>
          ))}
        </div>
      </div>

      {testimonials.length > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          <button
            type="button"
            onClick={scrollPrev}
            aria-label="Previous testimonial"
            className="rounded-full border border-border p-2 hover:bg-accent transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={scrollNext}
            aria-label="Next testimonial"
            className="rounded-full border border-border p-2 hover:bg-accent transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
      <span className="sr-only">{selected + 1}</span>
    </div>
  );
}
